import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Space, 
  Tag, 
  Button, 
  Card, 
  Typography, 
  Tabs,
  Tooltip,
  Popconfirm,
  message,
  Input,
  Badge
} from 'antd';
import { 
  ShareAltOutlined, 
  DeleteOutlined, 
  UserOutlined,
  EyeOutlined,
  EditOutlined,
  SearchOutlined,
  InboxOutlined,
  SendOutlined
} from '@ant-design/icons';
import { sharesAPI } from '../../services/api';
import moment from 'moment';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Search } = Input;

const SharesPage = () => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchShares();
  }, []);

  const fetchShares = async () => {
    try {
      setLoading(true);
      const response = await sharesAPI.getAll();
      if (response.data.success) {
        setShares(response.data.data);
      }
    } catch (error) {
      message.error('Erreur lors du chargement des partages');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShare = async (shareId) => {
    try {
      await sharesAPI.delete(shareId);
      message.success('Partage supprimé avec succès');
      fetchShares();
    } catch (error) {
      message.error('Erreur lors de la suppression du partage');
    }
  };

  const getFilteredShares = () => {
    let filtered = shares;

    // Filter by tab
    if (activeTab === 'sent') {
      filtered = filtered.filter(share => share.shared_by === share.currentUserId);
    } else if (activeTab === 'received') {
      filtered = filtered.filter(share => share.shared_with === share.currentUserId);
    }

    // Filter by search text
    if (searchText) {
      filtered = filtered.filter(share => 
        share.contact.first_name.toLowerCase().includes(searchText.toLowerCase()) ||
        share.contact.last_name.toLowerCase().includes(searchText.toLowerCase()) ||
        share.contact.email.toLowerCase().includes(searchText.toLowerCase()) ||
        share.recipient.first_name.toLowerCase().includes(searchText.toLowerCase()) ||
        share.recipient.last_name.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return filtered;
  };

  const columns = [
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text strong>{`${record.contact.first_name} ${record.contact.last_name}`}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.contact.email}
          </Text>
          {record.contact.company && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.contact.company}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Partagé avec',
      key: 'recipient',
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <span>{`${record.recipient.first_name} ${record.recipient.last_name}`}</span>
        </Space>
      )
    },
    {
      title: 'Email du destinataire',
      dataIndex: ['recipient', 'email'],
      key: 'recipient_email',
      render: (email) => <Text type="secondary">{email}</Text>
    },
    {
      title: 'Permission',
      dataIndex: 'permission',
      key: 'permission',
      render: (permission) => (
        <Tag color={permission === 'write' ? 'green' : 'blue'}>
          {permission === 'write' ? 'Écriture' : 'Lecture'}
          {permission === 'write' ? <EditOutlined style={{ marginLeft: 4 }} /> : <EyeOutlined style={{ marginLeft: 4 }} />}
        </Tag>
      )
    },
    {
      title: 'Date de partage',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <Tooltip title={moment(date).format('DD/MM/YYYY HH:mm')}>
          {moment(date).fromNow()}
        </Tooltip>
      ),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at)
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Êtes-vous sûr de vouloir supprimer ce partage ?"
          onConfirm={() => handleDeleteShare(record.id)}
          okText="Oui"
          cancelText="Non"
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            size="small"
          />
        </Popconfirm>
      )
    }
  ];

  const receivedColumns = [
    ...columns.slice(0, 1),
    {
      title: 'Partagé par',
      key: 'sharer',
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <span>{`${record.sharer.first_name} ${record.sharer.last_name}`}</span>
        </Space>
      )
    },
    ...columns.slice(3)
  ];

  const sentCount = shares.filter(share => share.shared_by === share.currentUserId).length;
  const receivedCount = shares.filter(share => share.shared_with === share.currentUserId).length;

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={2} style={{ margin: 0 }}>
              <ShareAltOutlined style={{ marginRight: 8 }} />
              Gestion des Partages
            </Title>
            <Space>
              <Badge count={sentCount} showZero>
                <Button icon={<SendOutlined />}>
                  Envoyés
                </Button>
              </Badge>
              <Badge count={receivedCount} showZero>
                <Button icon={<InboxOutlined />}>
                  Reçus
                </Button>
              </Badge>
            </Space>
          </div>

          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane 
              tab={
                <span>
                  <ShareAltOutlined />
                  Tous les partages
                </span>
              } 
              key="all"
            >
              <Search
                placeholder="Rechercher un contact ou un utilisateur..."
                allowClear
                enterButton={<SearchOutlined />}
                style={{ marginBottom: 16 }}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <Table
                columns={columns}
                dataSource={getFilteredShares()}
                rowKey="id"
                loading={loading}
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} sur ${total} partages`
                }}
                locale={{
                  emptyText: 'Aucun partage trouvé'
                }}
              />
            </TabPane>

            <TabPane 
              tab={
                <span>
                  <SendOutlined />
                  Partages envoyés ({sentCount})
                </span>
              } 
              key="sent"
            >
              <Search
                placeholder="Rechercher un contact ou un utilisateur..."
                allowClear
                enterButton={<SearchOutlined />}
                style={{ marginBottom: 16 }}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <Table
                columns={columns}
                dataSource={getFilteredShares()}
                rowKey="id"
                loading={loading}
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} sur ${total} partages envoyés`
                }}
                locale={{
                  emptyText: 'Aucun partage envoyé'
                }}
              />
            </TabPane>

            <TabPane 
              tab={
                <span>
                  <InboxOutlined />
                  Partages reçus ({receivedCount})
                </span>
              } 
              key="received"
            >
              <Search
                placeholder="Rechercher un contact ou un utilisateur..."
                allowClear
                enterButton={<SearchOutlined />}
                style={{ marginBottom: 16 }}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <Table
                columns={receivedColumns}
                dataSource={getFilteredShares()}
                rowKey="id"
                loading={loading}
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} sur ${total} partages reçus`
                }}
                locale={{
                  emptyText: 'Aucun partage reçu'
                }}
              />
            </TabPane>
          </Tabs>
        </Space>
      </Card>
    </div>
  );
};

export default SharesPage;
